import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  FormPageHeader,
  Input,
  Modal,
  ModalCloseButton,
  Select,
} from 'components/Shared';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import type { BuildingCondition, ListedBuilding } from 'contracts/Buildings';
import { useValidation } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  CreateBuildingActions,
  ListBuildingsActions,
  UpdateBuildingActions,
} from 'store/ducks/buildings';
import {
  CreateBuildingValidator,
  UpdateBuildingValidator,
} from 'validators/Buildings';
import * as S from './styles';

const conditionOptions = [
  { value: '', label: 'N/A' },
  { value: 'INFLAMÁVEL', label: 'Inflamável' },
  { value: 'REFRIGERADO', label: 'Refrigerado' },
  { value: 'QUÍMICO', label: 'Químico' },
];

interface Props {
  areaId: number | string;
}

/**
 * Os prédios são gerenciados dentro da área: o usuário já sabe em qual área
 * está, então o formulário nunca pede a área do prédio.
 */
export const BuildingsManager = ({ areaId }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleApiErrors, handleFormErrors } = useValidation();
  const [selected, setSelected] = useState<ListedBuilding | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { data, loading } = useSelector(
    (state: RootState) => state.listBuildings
  );
  const createState = useSelector((state: RootState) => state.createBuilding);
  const updateState = useSelector((state: RootState) => state.updateBuilding);

  const load = useCallback(() => {
    dispatch(ListBuildingsActions.request({ areaId }));
  }, [areaId, dispatch]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSelected(null);
    formRef.current?.reset();
    formRef.current?.setErrors({});
    dispatch(CreateBuildingActions.reset());
    dispatch(UpdateBuildingActions.reset());
  }, [dispatch]);

  const success = useCallback(() => {
    close();
    load();
  }, [close, load]);

  const open = (building?: ListedBuilding) => {
    setSelected(building ?? null);
    setIsOpen(true);
  };

  const submit = async (formData: {
    name: string;
    condition?: BuildingCondition | '';
  }) => {
    try {
      formRef.current?.setErrors({});
      const payload = { ...formData, areaId };
      const validator = selected
        ? new UpdateBuildingValidator()
        : new CreateBuildingValidator();
      const validData = await validator.schema.validate(payload, {
        abortEarly: false,
      });

      if (selected) {
        dispatch(
          UpdateBuildingActions.request(selected.id, validData, success)
        );
      } else {
        dispatch(CreateBuildingActions.request(validData, success));
      }
    } catch (error) {
      handleFormErrors(error, formRef);
    }
  };

  useEffect(() => load(), [load]);

  useEffect(() => {
    if (!isOpen) return;
    formRef.current?.setData({ name: selected?.name ?? '' });
    formRef.current?.setFieldValue(
      'condition',
      conditionOptions.find(
        ({ value }) => value === (selected?.condition ?? '')
      ) ?? null
    );
  }, [isOpen, selected]);

  useEffect(() => {
    handleApiErrors(
      selected ? updateState.validationErrors : createState.validationErrors,
      formRef
    );
  }, [
    createState.validationErrors,
    handleApiErrors,
    selected,
    updateState.validationErrors,
  ]);

  useEffect(
    () => () => {
      dispatch(ListBuildingsActions.reset());
      dispatch(CreateBuildingActions.reset());
      dispatch(UpdateBuildingActions.reset());
    },
    [dispatch]
  );

  const saving = createState.loading || updateState.loading;

  return (
    <S.MainPanel>
      <FormPageHeader
        title="Prédios"
        icon={<S.BuildingIcon />}
        isLoading={loading}
        actions={
          <S.Button size="small" onClick={() => open()}>
            <S.PlusIcon /> Novo prédio
          </S.Button>
        }
      />
      {!data.length ? (
        <S.EmptyListPlaceholder>
          Nenhum prédio cadastrado
        </S.EmptyListPlaceholder>
      ) : (
        <S.List>
          <S.ListHeader>
            <div>ID</div>
            <div>NOME</div>
            <div>CONDIÇÃO</div>
            <div />
          </S.ListHeader>
          {data.map((building) => (
            <S.ListItem key={building.id}>
              <S.Column>{building.id}</S.Column>
              <S.Column>{building.name}</S.Column>
              <S.Column>{building.condition ?? 'N/A'}</S.Column>
              <S.ActionsColumn>
                <S.ActionButton
                  aria-label={`Editar prédio ${building.name}`}
                  onClick={() => open(building)}
                >
                  <S.EditIcon />
                </S.ActionButton>
              </S.ActionsColumn>
            </S.ListItem>
          ))}
        </S.List>
      )}
      <Modal isOpen={isOpen} onClickOutside={close}>
        <S.ModalContent style={{ maxWidth: '540px' }}>
          <S.ModalHeader>
            <h4>{selected ? 'Editar prédio' : 'Novo prédio'}</h4>
            <ModalCloseButton onClick={close} />
          </S.ModalHeader>
          <S.ModalBody>
            <Form ref={formRef} onSubmit={submit}>
              <Input name="name" label="Nome" />
              <Select
                name="condition"
                label="Condição"
                options={conditionOptions}
                isClearable
                menuPortalTarget={document.body}
              />
              <S.FormActions>
                <S.Button type="button" mood="light" onClick={close}>
                  {MODAL_DISMISS_ACTION}
                </S.Button>
                <S.Button type="submit">
                  {saving ? <S.ActivityIndicator /> : 'Salvar'}
                </S.Button>
              </S.FormActions>
            </Form>
          </S.ModalBody>
        </S.ModalContent>
      </Modal>
    </S.MainPanel>
  );
};
